import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import FeedbackTextareaButtons from './FeedbackTextareaButtons';
import { useClient } from 'shared/apollo';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { FeedbackCommentProps, FeedbackSectionId, UserType } from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { postQuestionLevelComment, SaveResponse } from './api';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import {
  feedbackSectionIdToString,
  INDICATOR_SUMMARY_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import FeedbackModelButtons, { ModelDetails } from './FeedbackModelButtons';
import { IndicatorSectionKey } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { IndicatorDetails } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { SuccessResponse } from 'npo/pages/ReportsBeta/NewReport/api';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { ReportBuilderData } from 'shared/providers/ReportProvider/utils';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

export const parseFeedbackModelDetails = (
  details?: string,
): {
  indicatorId?: number;
  indicatorName?: string;
  sectionId?: IndicatorSectionKey;
} => {
  if (!details) return {};
  try {
    const { indicatorId, sectionId, indicatorName } = JSON.parse(details);
    return { indicatorId, sectionId, indicatorName };
  } catch (e) {
    return {};
  }
};

const getDetails = (
  details: string | undefined,
  modelDetails: ModelDetails,
): string | undefined => {
  return modelDetails.sectionId || modelDetails.indicator
    ? JSON.stringify({
        sectionId: modelDetails.sectionId,
        indicatorId: modelDetails.indicator?.indicatorId,
        indicatorName: modelDetails.indicator?.indicatorName,
      })
    : details;
};

interface FeedbackCommentTextareaProps {
  reportId: number;
  questionId: number;
  currentUserId: string | undefined;
  currentUserEmail: string | undefined;
  marginLeft?: string;
  marginRight?: string;
  userName: string;
  userType: UserType;
  sectionId: FeedbackSectionId;
  details?: string;
  answer?: string;
  className?: string;
  hideModelButton?: boolean;
  onSaveSuccess: (questionId: number, comment: FeedbackCommentProps) => unknown;
}

const StyledDiv = styled.div<{ marginLeft?: string; marginRight?: string }>`
  background-color: ${COLORS.whiteBase};
  border-radius: 4px;
  border: 1px solid ${COLORS.grayLight};
  ${({ marginLeft }) => (marginLeft ? `margin-left: ${marginLeft};` : '')}
  ${({ marginRight }) =>
    marginRight ? `margin-right: ${marginRight};` : ''}
  overflow: hidden;
  textarea {
    padding: ${SPACERS.small};
    border: none;
    outline: none;
    width: 100%;
    min-height: 100px;
    resize: vertical;
  }
  .text-section {
    width: 100%;
    display: inline-block;
    border-radius: 4px 4px 0 0;
    border: 1px solid ${COLORS.greyLight};
    transition: border 0.5s ease;
    &:focus-within {
      border: 1px solid ${COLORS.ctaPrimary};
    }
  }
  .trigger-update {
    display: flex;
    align-items: center;
    margin: ${SPACERS.small};
    background-color: ${COLORS.ctaPrimaryBackground};
    padding: 6px ${SPACERS.small} 6px 7px;
    border-radius: 4px;
    cursor: pointer;
    .checkbox {
      div {
        background-color: ${COLORS.whiteBase};
      }
      svg,
      span {
        border-color: ${COLORS.ctaPrimary};
      }
    }
  }
`;

const FeedbackCommentTextarea: React.FC<FeedbackCommentTextareaProps> = ({
  reportId,
  sectionId,
  answer,
  currentUserId,
  marginLeft,
  marginRight,
  currentUserEmail,
  details,
  userName,
  userType,
  questionId,
  className,
  hideModelButton,
  onSaveSuccess,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userId } = useUserActions();
  const { report, isReportEndDatePassed } = useReport();
  const [modelDetails, setModelDetails] = useState<ModelDetails>({});
  const [requestState, makeRequestState, setRequestState] = useGraphQLRequest<
    SuccessResponse<'newComment'>
  >();
  const [text, setText] = useState<string>('');

  const onClear = useCallback(() => {
    setText('');
  }, [setText]);

  useEffect(() => {
    if (
      requestState.type !== 'REQUEST_SUCCESS' ||
      !requestState.data.newComment.success
    ) {
      return;
    }
    const comment = {
      commentId: requestState.data.newComment.id,
      createdAt: new Date().toDateString(),
      name: userName,
      text,
      userId: Number(currentUserId || userId),
      email: currentUserEmail,
      accountType: userType,
      details: getDetails(details, modelDetails),
    };
    onSaveSuccess(questionId, comment);
    const eventPayload = {
      ...comment,
      ...pullReportData(
        report?.data || ({} as ReportBuilderData),
        isReportEndDatePassed,
        userType,
      ),
      questionId,
      section: feedbackSectionIdToString(sectionId),
    };
    trackEvent('FeedbackComment', eventPayload);
    onClear();
    setRequestState(requestInit());
  }, [requestState, details, onClear, setRequestState]);

  const handleText = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setText(value);
    },
    [setText],
  );

  const handleSave = () => {
    makeRequestState(
      postQuestionLevelComment(
        client!,
        reportId,
        questionId,
        text,
        sectionId,
        getDetails(details, modelDetails),
      ),
    );
  };

  const isEmpty = text.length === 0;

  return (
    <StyledDiv
      marginLeft={marginLeft}
      marginRight={marginRight}
      className={className}
    >
      <div className="text-section">
        <textarea
          value={text}
          onChange={handleText}
          rows={4}
          placeholder="Leave feedback here..."
        />
      </div>
      {userType === 'ti staff' &&
        answer &&
        !hideModelButton &&
        questionId === INDICATOR_SUMMARY_QUESTION_ID && (
          <FeedbackModelButtons
            answer={answer}
            modelDetails={modelDetails}
            setModelDetails={setModelDetails}
          />
        )}
      <FeedbackTextareaButtons
        userType={userType}
        handleSave={handleSave}
        errorMessage={
          requestState.type === 'REQUEST_ERROR'
            ? requestState.error.message
            : undefined
        }
        isSaving={requestState.type === 'REQUEST_START'}
        onClear={onClear}
        isEmpty={isEmpty}
        handleChange={setText}
      />
    </StyledDiv>
  );
};

export default FeedbackCommentTextarea;
